import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Sparkles, ShieldCheck, Truck, Star, ArrowRight } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { PRODUCTS, TESTIMONIALS, HERO_IMAGE, ABOUT_IMAGE, EDITORIAL_IMAGE } from "../mock";
import { fetchProducts } from "../api";

const values = [
  { icon: Leaf, title: "Ethically Sourced", text: "Directly from the women of Chad" },
  { icon: Sparkles, title: "Unrefined & Pure", text: "No fillers, no compromise" },
  { icon: ShieldCheck, title: "Cruelty Free", text: "Kind to you and the earth" },
  { icon: Truck, title: "Free Shipping", text: "On all orders over $60" }
];

const Home = () => {
  const [products, setProducts] = useState(PRODUCTS);

  useEffect(() => {
    fetchProducts()
      .then((data) => {
        if (Array.isArray(data) && data.length) setProducts(data);
      })
      .catch(() => {});
  }, []);

  const featured = products.slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden -mt-[92px] pt-[92px]">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Natural hair beauty" className="w-full h-full object-cover kn-kenburns" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0a09] via-[#0b0a09]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0a09] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-[1280px] mx-auto px-6 w-full">
          <div className="max-w-xl">
            <p className="kn-fade-up text-[#c9a24b] text-sm tracking-[0.35em] uppercase mb-6">
              Rooted in Tradition
            </p>
            <h1 className="kn-fade-up kn-delay-1 font-display text-[52px] md:text-[76px] leading-[0.98] text-[#f4efe4] font-medium">
              The Ancient Ritual of <span className="text-gold-gradient italic">Natural Beauty</span>
            </h1>
            <p className="kn-fade-up kn-delay-2 text-[#c8c0b2] text-lg mt-7 leading-relaxed max-w-md">
              Premium hair, body and beard care crafted with authentic African
              ingredients. Chebe, raw shea and botanical oils for moisture,
              strength and lasting glow.
            </p>
            <div className="kn-fade-up kn-delay-3 flex flex-wrap gap-4 mt-10">
              <Link to="/shop" className="btn-gold px-9 py-4 rounded-full text-sm tracking-[0.18em] uppercase font-medium">
                Shop the Collection
              </Link>
              <Link to="/about" className="btn-outline-gold px-9 py-4 rounded-full text-sm tracking-[0.18em] uppercase">
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES BAR */}
      <section className="bg-[#141210] border-y border-[#2c2822]">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#2c2822]">
          {values.map((v, i) => (
            <div key={i} className="flex items-center gap-4 py-8 px-5">
              <v.icon size={30} className="text-[#c9a24b] shrink-0" strokeWidth={1.4} />
              <div>
                <p className="text-[#f4efe4] text-sm font-medium tracking-wide">{v.title}</p>
                <p className="text-[#a29a8c] text-xs mt-1">{v.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED COLLECTION */}
      <section className="max-w-[1280px] mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-4">The Collection</p>
            <h2 className="font-display text-[40px] md:text-[52px] text-[#f4efe4] leading-tight max-w-md">
              Curated for the modern ritual
            </h2>
          </div>
          <Link to="/shop" className="kn-link inline-flex items-center gap-2 text-[#c9a24b] text-sm tracking-[0.15em] uppercase self-start md:self-auto">
            View all products <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* BRAND STORY SPLIT */}
      <section className="bg-[#141210] border-y border-[#2c2822]">
        <div className="max-w-[1280px] mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={ABOUT_IMAGE} alt="Chebe hair ritual" className="w-full aspect-[4/5] object-cover rounded-lg" />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-[#0b0a09] border border-[#c9a24b]/40 rounded-lg px-8 py-6">
              <p className="font-display text-4xl text-gold-gradient">100+</p>
              <p className="text-[#a29a8c] text-xs tracking-[0.15em] uppercase mt-1">Years of Tradition</p>
            </div>
          </div>
          <div>
            <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-5">Our Heritage</p>
            <h2 className="font-display text-[38px] md:text-[50px] text-[#f4efe4] leading-tight mb-7">
              A secret carried by <span className="italic text-gold-gradient">generations</span>
            </h2>
            <p className="text-[#c8c0b2] leading-relaxed mb-5">
              For centuries, the women of the Basara tribe in Chad have used Chebe
              to grow and protect some of the longest, healthiest hair in the world.
              Kanama Naturals brings this sacred ritual to you — unchanged, unrefined,
              and honestly made.
            </p>
            <p className="text-[#a29a8c] leading-relaxed mb-9">
              Every jar is sourced ethically and prepared in small batches, so the
              tradition arrives at your door exactly as nature intended.
            </p>
            <Link to="/about" className="btn-outline-gold inline-flex px-9 py-4 rounded-full text-sm tracking-[0.18em] uppercase">
              Discover Our Story
            </Link>
          </div>
        </div>
      </section>

      {/* EDITORIAL CTA */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={EDITORIAL_IMAGE} alt="Editorial beauty" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0b0a09]/80" />
        </div>
        <div className="relative max-w-[760px] mx-auto px-6 text-center">
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-6">Your Satisfaction, Our Priority</p>
          <h2 className="font-display text-[40px] md:text-[58px] text-[#f4efe4] leading-tight mb-8">
            Nature's finest, refined for the ones who expect more
          </h2>
          <Link to="/shop" className="btn-gold inline-flex px-10 py-4 rounded-full text-sm tracking-[0.18em] uppercase font-medium">
            Begin Your Ritual
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1280px] mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-4">Loved Worldwide</p>
          <h2 className="font-display text-[40px] md:text-[52px] text-[#f4efe4]">
            Words from our community
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-[#141210] border border-[#2c2822] rounded-lg p-8 card-hover">
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} size={16} className="fill-[#c9a24b] text-[#c9a24b]" />
                ))}
              </div>
              <p className="text-[#c8c0b2] leading-relaxed italic mb-7">“{t.text}”</p>
              <div className="gold-hairline mb-5" />
              <p className="text-[#f4efe4] font-medium">{t.name}</p>
              <p className="text-[#a29a8c] text-sm">{t.location}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
